import React, { useEffect, useRef } from 'react';

export const WireframeCube = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let rotationX = 0;
    let rotationY = 0;
    let targetRotationX = 0;
    let targetRotationY = 0;

    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

    const getOpacity = () => {
      if (isMobile()) {
        return {
          grid: 0.06,
          outer: 0.42,
          inner: 0.22,
        };
      }
      return {
        grid: 0.035,
        outer: 0.26,
        inner: 0.14,
      };
    };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const cubeSize = 150;
    const innerCubeSize = 75;

    const createCubeVertices = (size) => [
      [-size, -size, -size],
      [size, -size, -size],
      [size, size, -size],
      [-size, size, -size],
      [-size, -size, size],
      [size, -size, size],
      [size, size, size],
      [-size, size, size],
    ];

    const cubeEdges = [
      [0, 1], [1, 2], [2, 3], [3, 0],
      [4, 5], [5, 6], [6, 7], [7, 4],
      [0, 4], [1, 5], [2, 6], [3, 7],
    ];

    const rotateX = (point, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return [
        point[0],
        point[1] * cos - point[2] * sin,
        point[1] * sin + point[2] * cos,
      ];
    };

    const rotateY = (point, angle) => {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return [
        point[0] * cos + point[2] * sin,
        point[1],
        -point[0] * sin + point[2] * cos,
      ];
    };

    const project = (point) => {
      const fov = 500;
      const z = point[2] + 400;
      const scale = fov / z;
      return [
        point[0] * scale + canvas.width / 2,
        point[1] * scale + canvas.height / 2,
      ];
    };

    const drawCube = (vertices, opacity) => {
      ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.lineWidth = 1;
      ctx.beginPath();

      cubeEdges.forEach(([start, end]) => {
        let p1 = vertices[start];
        let p2 = vertices[end];

        p1 = rotateX(p1, rotationX);
        p1 = rotateY(p1, rotationY);
        p2 = rotateX(p2, rotationX);
        p2 = rotateY(p2, rotationY);

        const [x1, y1] = project(p1);
        const [x2, y2] = project(p2);

        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      });

      ctx.stroke();
    };

    const drawGrid = () => {
      const { grid } = getOpacity();

      const gridSize = 300;
      const divisions = 8;
      const step = gridSize / divisions;
      const y = 100;

      ctx.strokeStyle = `rgba(255, 255, 255, ${grid})`;
      ctx.lineWidth = 1;
      ctx.beginPath();

      for (let i = -gridSize / 2; i <= gridSize / 2; i += step) {
        let p1 = rotateX([-gridSize / 2, y, i], rotationX * 0.3);
        let p2 = rotateX([gridSize / 2, y, i], rotationX * 0.3);
        p1 = rotateY(p1, rotationY * 0.3);
        p2 = rotateY(p2, rotationY * 0.3);

        const [x1, y1] = project(p1);
        const [x2, y2] = project(p2);

        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);

        p1 = rotateX([i, y, -gridSize / 2], rotationX * 0.3);
        p2 = rotateX([i, y, gridSize / 2], rotationX * 0.3);
        p1 = rotateY(p1, rotationY * 0.3);
        p2 = rotateY(p2, rotationY * 0.3);

        const [x3, y3] = project(p1);
        const [x4, y4] = project(p2);

        ctx.moveTo(x3, y3);
        ctx.lineTo(x4, y4);
      }

      ctx.stroke();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const { outer, inner } = getOpacity();

      targetRotationX = mouseRef.current.y * 0.3;
      targetRotationY = mouseRef.current.x * 0.5;

      rotationX += (targetRotationX - rotationX) * 0.05;
      rotationY += (targetRotationY - rotationY) * 0.05;

      rotationX += 0.001;
      rotationY += 0.002;

      drawGrid();

      drawCube(createCubeVertices(cubeSize), outer);

      const innerVertices = createCubeVertices(innerCubeSize).map(v => {
        let rotated = rotateX(v, 0.5);
        rotated = rotateY(rotated, 0.3);
        return rotated;
      });

      ctx.strokeStyle = `rgba(255, 255, 255, ${inner})`;
      ctx.beginPath();

      cubeEdges.forEach(([start, end]) => {
        let p1 = innerVertices[start];
        let p2 = innerVertices[end];

        p1 = rotateX(p1, rotationX * 1.2);
        p1 = rotateY(p1, rotationY * 1.2);
        p2 = rotateX(p2, rotationX * 1.2);
        p2 = rotateY(p2, rotationY * 1.2);

        const [x1, y1] = project(p1);
        const [x2, y2] = project(p2);

        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      });

      ctx.stroke();

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleMouseMove = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
      aria-label="Interactive 3D wireframe cube background"
    />
  );
};

export default WireframeCube;
